import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Upload, Loader2, Trash2, Pencil, Check, X, Plus, ExternalLink, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PodcastQueue, PodcastList, EpisodeQueue } from "../components/AdminPodcasts";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const emptyFilm = { id: "", title: "", director: "", synopsis: "", year: "", runtime: "", genres: "", poster: "", backdrop: "", trailer_url: "", watch_url: "", watch_label: "Watch now" };

const field = "w-full rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]";
const lbl = "mb-1 block text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)]";

function FilmForm({ initial, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const editing = !!initial.id;

  const uploadPoster = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, poster: `${BACKEND}${data.url}` }));
      toast.success("Poster uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        title: f.title, director: f.director, synopsis: f.synopsis,
        year: f.year ? parseInt(f.year, 10) : null, runtime: f.runtime ? parseInt(f.runtime, 10) : null,
        genres: (typeof f.genres === "string" ? f.genres.split(",") : f.genres).map((g) => g.trim()).filter(Boolean),
        poster: f.poster, backdrop: f.backdrop, trailer_url: f.trailer_url, watch_url: f.watch_url, watch_label: f.watch_label || "Watch now",
      };
      if (editing) await api.put(`/admin/indie/${f.id}`, payload);
      else await api.post("/admin/indie", payload);
      toast.success(editing ? "Film updated" : "Film added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="rounded-[var(--card-radius)] border border-[var(--border-accent)] bg-[var(--bg-card)] p-5" data-testid="admin-film-form">
      <h3 className="font-head text-xl text-[var(--text-primary)]">{editing ? `Edit: ${initial.title}` : "Add a new indie film"}</h3>
      <div className="mt-4 space-y-4">
        <div><label className={lbl}>Title *</label><input className={field} value={f.title} onChange={set("title")} data-testid="admin-film-title" /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className={lbl}>Director</label><input className={field} value={f.director} onChange={set("director")} /></div>
          <div><label className={lbl}>Genres (comma-sep)</label><input className={field} value={Array.isArray(f.genres) ? f.genres.join(", ") : f.genres} onChange={set("genres")} /></div>
        </div>
        <div><label className={lbl}>Synopsis</label><textarea className={`${field} min-h-[90px]`} value={f.synopsis} onChange={set("synopsis")} /></div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div><label className={lbl}>Year</label><input className={field} value={f.year || ""} onChange={set("year")} /></div>
          <div><label className={lbl}>Runtime</label><input className={field} value={f.runtime || ""} onChange={set("runtime")} /></div>
          <div><label className={lbl}>Watch label</label><input className={field} value={f.watch_label} onChange={set("watch_label")} /></div>
        </div>
        <div><label className={lbl}>Watch URL *</label><input className={field} value={f.watch_url} onChange={set("watch_url")} data-testid="admin-film-watch-url" /></div>
        <div><label className={lbl}>Trailer URL</label><input className={field} value={f.trailer_url} onChange={set("trailer_url")} /></div>
        <div><label className={lbl}>Backdrop URL</label><input className={field} value={f.backdrop} onChange={set("backdrop")} /></div>
        <div>
          <label className={lbl}>Poster</label>
          <div className="flex items-center gap-3">
            {f.poster ? <img src={f.poster} alt="" className="h-24 w-16 rounded object-cover" /> : <div className="flex h-24 w-16 items-center justify-center rounded border border-dashed border-[var(--border-subtle)] text-[var(--text-muted)]">?</div>}
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-2 text-sm text-[var(--text-primary)] hover:border-[var(--accent)]">
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
              <input type="file" accept="image/*" className="hidden" onChange={uploadPoster} disabled={uploading} />
            </label>
            <input className={field} value={f.poster} onChange={set("poster")} placeholder="or paste URL" />
          </div>
        </div>
        <div className="flex gap-3">
          <button onClick={save} disabled={saving || uploading || !f.title || !f.watch_url} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-5 py-2.5 font-bold uppercase tracking-wide text-[var(--accent-contrast)] disabled:opacity-60" data-testid="admin-film-save">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} {editing ? "Save changes" : "Add film"}
          </button>
          <button onClick={onCancel} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-5 py-2.5 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="admin-film-cancel"><X size={16} /> Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [films, setFilms] = useState([]);
  const [editing, setEditing] = useState(null); // film object or emptyFilm or null
  const [access, setAccess] = useState({ approvals: [], requests: [] });
  const [emailInput, setEmailInput] = useState("");
  const [busyAccess, setBusyAccess] = useState(() => new Set());
  const [reports, setReports] = useState([]);
  const [podSubs, setPodSubs] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [episodes, setEpisodes] = useState([]);

  const load = useCallback(async () => {
    try {
      const [s, f, a, r, ps, pl, pe] = await Promise.all([api.get("/admin/submissions"), api.get("/admin/indie"), api.get("/admin/access"), api.get("/admin/community-reports"), api.get("/admin/podcast-submissions"), api.get("/admin/podcasts"), api.get("/admin/podcast-episodes")]);
      setSubs(s.data.submissions); setFilms(f.data.films); setAccess(a.data); setReports(r.data.reports || []);
      setPodSubs(ps.data.submissions || []); setPodcasts(pl.data.podcasts || []); setEpisodes(pe.data.episodes || []);
    } catch { /* handled by guard */ }
  }, []);

  useEffect(() => { if (user && user.is_admin) load(); }, [user, load]);

  if (user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const pending = subs.filter((s) => s.status === "pending");
  const podPending = podSubs.filter((s) => s.status === "pending");

  const approve = async (id) => {
    const creator = window.prompt("Filmmaker's account to host the discussion space (email or @username). Leave blank to auto-match the submission email, or assign later.") ?? null;
    if (creator === null) return;
    try {
      await api.post(`/admin/submissions/${id}/approve`, creator.trim() ? { creator_identifier: creator.trim() } : {});
      toast.success("Approved & published — creator space created");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const resolveReport = async (rid) => { try { await api.post(`/admin/community-reports/${rid}/resolve`); toast.success("Report resolved"); load(); } catch { toast.error("Failed"); } };
  const reject = async (id) => { try { await api.post(`/admin/submissions/${id}/reject`); toast("Rejected"); load(); } catch (e) { toast.error("Failed"); } };
  const del = async (slug) => { if (!window.confirm("Delete this film?")) return; try { await api.delete(`/admin/indie/${slug}`); toast("Deleted"); load(); } catch (e) { toast.error("Failed"); } };
  const setAccessFor = async (email, full_access) => {
    if (busyAccess.has(email)) return;
    setBusyAccess((p) => new Set(p).add(email));
    try { const { data } = await api.post("/admin/access", { email, full_access }); setAccess(data); toast.success(full_access ? `Full Access granted to ${email}` : `Access removed for ${email}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusyAccess((p) => { const n = new Set(p); n.delete(email); return n; }); }
  };
  const grant = () => { const e = emailInput.trim().toLowerCase(); if (!e) return; setAccessFor(e, true); setEmailInput(""); };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-8 lg:px-8" data-testid="admin-page">
        <h1 className="font-head text-4xl text-[var(--text-primary)]">Indie Admin</h1>
        <p className="mt-1 text-sm text-[var(--text-secondary)]">Review filmmaker submissions and manage the films that appear in the Indie Spotlight.</p>

        {/* Full Access approvals */}
        <section className="mt-8" data-testid="admin-access">
          <h2 className="font-head text-2xl text-[var(--text-primary)]">Full Access approvals</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">Grant Full Access (all 7 skins) by email. Unregistered emails are held and applied automatically when they sign up.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder="email@example.com" className="min-w-[240px] flex-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-3 py-2.5 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]" data-testid="access-email-input" />
            <button onClick={grant} className="rounded-[var(--card-radius)] bg-[var(--accent)] px-5 py-2.5 font-bold uppercase tracking-wide text-[var(--accent-contrast)]" data-testid="access-grant-button">Grant Full Access</button>
          </div>

          {access.approvals.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="access-list">
              {access.approvals.map((a) => (
                <div key={a.email} className="flex flex-wrap items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3" data-testid={`access-row-${a.email}`}>
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-primary)]">{a.email}</span>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${a.registered ? "bg-[var(--accent)]/15 text-[var(--accent-hover)]" : "bg-[var(--text-muted)]/20 text-[var(--text-secondary)]"}`}>{a.registered ? "Registered" : "Pending"}</span>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${a.full_access ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>{a.full_access ? "Full Access" : "Free"}</span>
                  <button onClick={() => setAccessFor(a.email, !a.full_access)} disabled={busyAccess.has(a.email)} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-semibold text-[var(--text-primary)] hover:border-[var(--accent)] disabled:opacity-50" data-testid={`access-toggle-${a.email}`}>{busyAccess.has(a.email) && <Loader2 size={12} className="animate-spin" />}{a.full_access ? "Revoke" : "Grant"}</button>
                </div>
              ))}
            </div>
          )}

          {access.requests.length > 0 && (
            <div className="mt-5" data-testid="access-requests">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]">Requests ({access.requests.length})</h3>
              <div className="mt-2 space-y-2">
                {access.requests.map((r, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3" data-testid="access-request-row">
                    <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-primary)]">{r.email} {r.skin ? <span className="text-[var(--text-muted)]">· wants {r.skin}</span> : null}</span>
                    <span className="text-xs text-[var(--text-muted)]">{(r.created_at || "").slice(0, 10)}</span>
                    <button onClick={() => setAccessFor(r.email, true)} disabled={busyAccess.has(r.email)} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] bg-[var(--accent)] px-3 py-1.5 text-xs font-bold uppercase text-[var(--accent-contrast)] disabled:opacity-50" data-testid="access-request-grant">{busyAccess.has(r.email) && <Loader2 size={12} className="animate-spin" />}Grant</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Submissions */}
        <section className="mt-8" data-testid="admin-submissions">
          <h2 className="font-head text-2xl text-[var(--text-primary)]">Submissions <span className="text-[var(--text-muted)]">({pending.length + podPending.length + episodes.length} pending)</span></h2>
          <h3 className="mt-4 text-xs font-bold uppercase tracking-widest text-[var(--text-secondary)]" data-testid="admin-film-queue-heading">Film queue ({pending.length})</h3>
          {pending.length === 0 ? (
            <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="admin-no-submissions">No pending submissions.</p>
          ) : (
            <div className="mt-4 space-y-3">
              {pending.map((s) => (
                <SubmissionCard key={s.id} s={s} onApprove={approve} onReject={reject} />
              ))}
            </div>
          )}
          <PodcastQueue subs={podPending} onChange={load} />
          <EpisodeQueue episodes={episodes} onChange={load} />
        </section>

        {/* Community reports */}
        <section className="mt-8" data-testid="admin-community-reports">
          <h2 className="font-head text-2xl text-[var(--text-primary)]">Community reports <span className="text-[var(--text-muted)]">({reports.length})</span></h2>
          {reports.length === 0 ? (
            <p className="mt-3 text-sm text-[var(--text-secondary)]" data-testid="admin-no-reports">No open reports.</p>
          ) : (
            <div className="mt-4 space-y-2">
              {reports.map((r) => (
                <div key={r.id} className="flex flex-wrap items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3" data-testid={`report-${r.id}`}>
                  <span className="rounded bg-[var(--text-muted)]/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-[var(--text-secondary)]">{r.target_type}</span>
                  <span className="min-w-0 flex-1 truncate text-sm text-[var(--text-primary)]" title={r.snippet}>{r.snippet || "—"}{r.reason ? <span className="text-[var(--text-muted)]"> · “{r.reason}”</span> : null}</span>
                  <span className="text-xs text-[var(--text-muted)]">by {r.reporter_name}</span>
                  <button onClick={() => resolveReport(r.id)} className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-1.5 text-xs font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]" data-testid={`report-resolve-${r.id}`}>Resolve</button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Films */}
        <section className="mt-12" data-testid="admin-films">
          <div className="flex items-center justify-between">
            <h2 className="font-head text-2xl text-[var(--text-primary)]">Indie films <span className="text-[var(--text-muted)]">({films.length})</span></h2>
            {!editing && <button onClick={() => setEditing({ ...emptyFilm })} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] bg-[var(--accent)] px-4 py-2 font-bold uppercase tracking-wide text-[var(--accent-contrast)]" data-testid="admin-add-film"><Plus size={16} /> Add film</button>}
          </div>

          {editing && (
            <div className="mt-4">
              <FilmForm initial={editing} onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
            </div>
          )}

          <div className="mt-4 space-y-2">
            {films.map((film) => (
              <div key={film.id} className="flex items-center gap-4 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-3" data-testid={`admin-film-${film.id}`}>
                {film.poster ? <img src={film.poster} alt="" className="h-16 w-11 flex-none rounded object-cover" /> : null}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-[var(--text-primary)]">{film.title} {film.is_sample && <span className="ml-1 rounded bg-[var(--text-muted)]/30 px-1.5 py-0.5 text-[10px] uppercase text-[var(--text-secondary)]">Sample</span>}</p>
                  <p className="truncate text-xs text-[var(--text-secondary)]">{[film.year, film.director].filter(Boolean).join(" · ")}</p>
                </div>
                <button onClick={() => setEditing({ ...emptyFilm, ...film })} className="rounded p-2 text-[var(--text-secondary)] hover:text-[var(--accent)]" data-testid={`admin-edit-${film.id}`}><Pencil size={16} /></button>
                <button onClick={() => del(film.id)} className="rounded p-2 text-[var(--text-secondary)] hover:text-red-500" data-testid={`admin-delete-${film.id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </section>

        <PodcastList podcasts={podcasts} onChange={load} />
      </main>
    </div>
  );
}


function SubmissionCard({ s, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const rows = [
    ["Title", s.title],
    ["Year", s.year],
    ["Director", s.director],
    ["Email", s.contact_email],
    ["Runtime", s.runtime ? `${s.runtime} min` : ""],
    ["Genres", Array.isArray(s.genres) ? s.genres.join(", ") : s.genres],
    ["Synopsis", s.synopsis],
    ["Watch label", s.watch_label],
  ].filter(([, v]) => v !== undefined && v !== null && v !== "");

  return (
    <div className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4" data-testid={`admin-submission-${s.id}`}>
      <div className="flex items-start gap-4">
        {s.poster ? (
          <button type="button" onClick={() => setOpen((o) => !o)} className="flex-none" aria-label="Toggle submission details" data-testid={`admin-submission-poster-${s.id}`}>
            <img src={s.poster} alt={s.title} className="h-24 w-16 rounded object-cover" />
          </button>
        ) : null}
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={`admin-submission-details-${s.id}`}
            className="flex w-full items-start justify-between gap-2 text-left"
            data-testid={`admin-submission-toggle-${s.id}`}
          >
            <span className="font-head text-lg leading-snug text-[var(--text-primary)] break-words">
              {s.title} {s.year ? <span className="text-[var(--text-muted)]">({s.year})</span> : null}
            </span>
            <ChevronDown size={18} className={`mt-1 flex-none text-[var(--text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          <p className="mt-0.5 text-xs text-[var(--text-secondary)] break-words">Dir. {s.director || "—"} · {s.contact_email}</p>
          {s.watch_url ? (
            <a
              href={s.watch_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex max-w-full items-center gap-1 text-xs font-semibold text-[var(--accent)] underline underline-offset-2 hover:text-[var(--accent-hover)] break-all"
              data-testid={`admin-submission-watch-${s.id}`}
            >
              <ExternalLink size={13} className="flex-none" />
              <span className="break-all">{s.watch_label || "Watch now"} → {s.watch_url}</span>
            </a>
          ) : null}
        </div>
      </div>

      {open && (
        <div id={`admin-submission-details-${s.id}`} className="mt-3 space-y-2 border-t border-[var(--border-subtle)] pt-3" data-testid={`admin-submission-details-${s.id}`}>
          {rows.map(([label, val]) => (
            <div key={label} className="text-sm">
              <span className="text-[var(--text-muted)]">{label}: </span>
              <span className="text-[var(--text-primary)] whitespace-pre-wrap break-words">{String(val)}</span>
            </div>
          ))}
          {s.watch_url ? (
            <div className="text-sm">
              <span className="text-[var(--text-muted)]">Watch URL: </span>
              <a href={s.watch_url} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] underline underline-offset-2 break-all" data-testid={`admin-submission-watch-full-${s.id}`}>{s.watch_url}</a>
            </div>
          ) : null}
          {s.trailer_url ? (
            <div className="text-sm">
              <span className="text-[var(--text-muted)]">Trailer: </span>
              <a href={s.trailer_url} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] underline underline-offset-2 break-all">{s.trailer_url}</a>
            </div>
          ) : null}
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => onApprove(s.id)} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] bg-[var(--accent)] px-3 py-2 text-sm font-bold uppercase text-[var(--accent-contrast)]" data-testid={`admin-approve-${s.id}`}><Check size={15} /> Approve</button>
        <button onClick={() => onReject(s.id)} className="inline-flex items-center gap-1 rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-3 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid={`admin-reject-${s.id}`}><X size={15} /> Reject</button>
      </div>
    </div>
  );
}
